import { Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { tx } from '../i18n';

/**
 * The steps being run while a report is built, in the order the server runs them.
 *
 * <p>These are the real queries: the police API, the EPC register and Ofcom's coverage file are
 * asked together, and the score is written once all three are in. What the interface cannot know
 * is which one is still outstanding - the request is a single POST - so the list advances on
 * elapsed time against how long each step usually takes, and never claims a step finished that
 * the answer has not yet arrived for: the last one stays in progress until the report is really
 * there. No percentage is shown anywhere, because we would be making it up.
 */
const STEPS: Array<{ key: string; after: number; label: () => string }> = [
  {
    key: 'address',
    after: 0,
    label: () =>
      tx(
        'Finding the property',
        'Konut bulunuyor',
        'Wyszukiwanie lokalu',
        'A localizar o imóvel',
        'Localizando la vivienda',
      ),
  },
  {
    key: 'epc',
    after: 1200,
    label: () =>
      tx(
        'Checking the energy certificate',
        'Enerji sertifikası kontrol ediliyor',
        'Sprawdzanie świadectwa energetycznego',
        'A verificar o certificado energético',
        'Comprobando el certificado energético',
      ),
  },
  {
    key: 'crime',
    after: 3000,
    label: () =>
      tx(
        'Counting recorded crime nearby',
        'Çevredeki kayıtlı suçlar sayılıyor',
        'Liczenie odnotowanej przestępczości w okolicy',
        'A contar a criminalidade registada nas imediações',
        'Contando la delincuencia registrada en la zona',
      ),
  },
  {
    key: 'broadband',
    after: 5200,
    label: () =>
      tx(
        'Reading broadband coverage',
        'İnternet kapsaması okunuyor',
        'Odczytywanie zasięgu internetu',
        'A ler a cobertura de internet',
        'Leyendo la cobertura de internet',
      ),
  },
  {
    key: 'score',
    after: 7000,
    label: () =>
      tx(
        'Preparing your report',
        'Raporunuz hazırlanıyor',
        'Przygotowywanie raportu',
        'A preparar o seu relatório',
        'Preparando tu informe',
      ),
  },
];

/**
 * What the wait is for.
 *
 * <p>A spinner says "wait"; this says what is being waited on, which is the difference between a
 * slow product and a thorough one. It also sets expectations honestly: a reader who sees four
 * registers being asked understands why it is not instant.
 */
export default function AnalysisProgress({ addressLine }: { addressLine: string }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Date.now() - started), 400);
    return () => window.clearInterval(timer);
  }, [addressLine]);

  // The last step a reader has been shown; everything before it has had its answer.
  let reached = 0;
  STEPS.forEach((step, index) => {
    if (elapsed >= step.after) {
      reached = index;
    }
  });

  return (
    <div className="card progress-panel">
      <p className="progress-panel__title">
        {tx('Checking public records for', 'Kamu kayıtları kontrol ediliyor:',
          'Sprawdzanie rejestrów publicznych dla',
          'A consultar registos públicos de',
          'Consultando registros públicos de')}{' '}
        <strong>{addressLine}</strong>
      </p>

      <ol className="progress-steps" aria-live="polite">
        {STEPS.map((step, index) => {
          const done = index < reached;
          const active = index === reached;
          return (
            <li
              key={step.key}
              className={done ? 'progress-step progress-step--done' : active ? 'progress-step progress-step--active' : 'progress-step'}
            >
              <span className="progress-step__mark" aria-hidden="true">
                {done ? <Check size={14} /> : active ? <Loader2 size={14} className="spin" /> : null}
              </span>
              <span>{step.label()}</span>
              {active ? (
                <span className="sr-only">
                  {tx('in progress', 'sürüyor', 'w toku', 'em curso', 'en curso')}
                </span>
              ) : null}
              {done ? (
                <span className="sr-only">
                  {tx('done', 'tamamlandı', 'gotowe', 'concluído', 'hecho')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>

      <p className="small muted">
        {elapsed > 12_000
          ? tx(
              'One of the registers is being slow today. We wait up to 45 seconds before giving up on it.',
              'Kayıtlardan biri bugün yavaş. Vazgeçmeden önce 45 saniyeye kadar bekliyoruz.',
              'Jeden z rejestrów jest dziś powolny. Czekamy do 45 sekund, zanim odpuścimy.',
              'Um dos registos está lento hoje. Esperamos até 45 segundos antes de desistir.',
              'Hoy uno de los registros va lento. Esperamos hasta 45 segundos antes de rendirnos.',
            )
          : tx(
              'This usually takes a few seconds.',
              'Bu genellikle birkaç saniye sürer.',
              'Zwykle zajmuje to kilka sekund.',
              'Costuma demorar alguns segundos.',
              'Suele tardar unos segundos.',
            )}
      </p>
    </div>
  );
}
